import type { AppliedConfiguration } from '@/types/items';
import { resolvedModificationUuid } from '@/appdata/applied-configuration';
import { containerDisplayName } from '@/appdata/container-fields';
import { blankNormalise, composeDisplayName, SEPARATOR } from '@/appdata/container-display-name';
import { elementEntryUuid, supportedContainerPath } from '@/appdata/container-targets';

/**
 * The choices an element resolved, as words in its display name — "Elemental Scion (Air)",
 * "Trademark Weapon (Light Mace)". Display time only: everything is read from the records each
 * pick already leaves in additionalData.choices, and nothing is written. An explicit trapping
 * always wins (see containerDisplayName's fallback parameter); these labels only fill an empty slot.
 *
 * Own choice sets only: a record counts when it resolves a modification the element itself holds,
 * choice-set options included. An ancestry's grants resolve under the ancestry's uuid, so the key
 * alone would credit the ancestry with a granted ability's pick — the modification uuid is what
 * tells them apart.
 */

/** Every modification uuid a list holds, descending into choice-set option lists. */
const collectOwnModificationUuids = (modifications: unknown, sink: Set<string>): void => {
    if (!Array.isArray(modifications)) return;
    for (const mod of modifications) {
        const uuid = (mod as { uuid?: unknown } | null)?.uuid;
        if (typeof uuid === 'string') sink.add(uuid);
        const element = (mod as { configuration?: { element?: unknown } } | null)?.configuration?.element;
        if (Array.isArray(element)) collectOwnModificationUuids(element, sink);
    }
};

/**
 * A container-modification record is labelled by what it points at NOW — a renamed target follows,
 * a pick-many lists each target still on the sheet — and only when no target is left by the label
 * stored at pick time.
 */
const targetLabels = (character: unknown, record: AppliedConfiguration): string[] => {
    const arrayName = supportedContainerPath(record.configuration?.path);
    if (arrayName === undefined) return [];
    const containers = (character as Record<string, unknown> | null)?.[arrayName];
    const list = Array.isArray(containers) ? containers : [];
    const element = record.configuration?.element;
    const entries = Array.isArray(element) ? element : [element];
    return entries
        .map(elementEntryUuid)
        .map((uuid) => uuid === null ? null : list.find((c: any) => c?.uuid === uuid))
        .filter((target) => target !== null && target !== undefined)
        .map((target) => blankNormalise(containerDisplayName(target)))
        .filter((label): label is string => label !== null);
};

export function chosenLabels(character: unknown, sourceUuid: string | null | undefined, ownModifications: unknown): string[] {
    if (!sourceUuid) return [];
    const own = new Set<string>();
    collectOwnModificationUuids(ownModifications, own);
    if (own.size === 0) return [];

    const records = (character as { additionalData?: { choices?: unknown } } | null)?.additionalData?.choices;
    const labels: string[] = [];
    for (const record of (Array.isArray(records) ? records : []) as AppliedConfiguration[]) {
        if (record?.uuid !== sourceUuid) continue;
        const modUuid = resolvedModificationUuid(record);
        if (modUuid === undefined || !own.has(modUuid)) continue;
        const stored = blankNormalise(record.configuration?.label ?? null);
        const resolved = supportedContainerPath(record.configuration?.path) === undefined
            ? (stored === null ? [] : [stored])
            : (() => { const targets = targetLabels(character, record); return targets.length > 0 ? targets : (stored === null ? [] : [stored]); })();
        for (const label of resolved) if (!labels.includes(label)) labels.push(label);
    }
    return labels;
}

/** chosenLabels joined the way the composer joins its parts, or null when there are none. */
const chosenTrapping = (character: unknown, sourceUuid: string | null | undefined, ownModifications: unknown): string | null => {
    const labels = chosenLabels(character, sourceUuid, ownModifications);
    return labels.length === 0 ? null : labels.join(SEPARATOR);
};

/** containerDisplayName for a container on the sheet, its chosen labels filling an empty trapping. */
export function containerDisplayNameFor(character: unknown, container: unknown): string {
    const source = (container ?? null) as { uuid?: unknown, selection?: { modifications?: unknown }, skill?: { modifications?: unknown }, hindrance?: { modifications?: unknown } } | null;
    const catalog = source?.selection ?? source?.skill ?? source?.hindrance;
    const uuid = typeof source?.uuid === 'string' ? source.uuid : null;
    return containerDisplayName(container, chosenTrapping(character, uuid, catalog?.modifications));
}

/**
 * The display name of a catalog element the sheet holds directly — the ancestry, a special ability —
 * with its chosen labels in parentheses. `sourceUuid` names whose records to read when the picks
 * were applied under another element's uuid: an ancestry's grants resolve under the ancestry.
 */
export function elementDisplayName(character: unknown, element: unknown, sourceUuid?: string | null): string {
    const source = (element ?? null) as { uuid?: unknown, name?: unknown, modifications?: unknown } | null;
    const uuid = sourceUuid ?? (typeof source?.uuid === 'string' ? source.uuid : null);
    const name = typeof source?.name === 'string' ? source.name : null;
    return composeDisplayName(name, null, null, chosenTrapping(character, uuid, source?.modifications));
}
